import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { useFeatureEnabled } from "../hooks/useFeatureFlag";
import {
  ReturnNotification,
  ReturnPoliciesResponse,
  ReturnTemplate,
  ReturnsSettings,
  WarehouseCheckContext,
} from "../models/returns/IReturnsWarehouseApi";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";
import isLocalDebug from "../utils/isLocalDebug";

/** The customer feature flag, spelt as Betty has it. */
export const RETURNS_FEATURE = "Returns";

/**
 * True when this Wilma may call the returns endpoints: the flag is on (or it's a local debug run).
 * Every returns query checks it, so a page opened by a typed URL on a Wilma without the flag
 * shows "No access" without calling (or polling) the API.
 */
export function useReturnsLive() {
  const enabled = useFeatureEnabled(RETURNS_FEATURE);
  return enabled || isLocalDebug();
}

/**
 * Every key starts with "returns" so the other hooks' returnsKeys.all (["returns"]) prefix covers
 * them too: any returns write invalidates the check context along with the list and detail.
 */
export const returnsWarehouseKeys = {
  all: ["returns"] as const,
  warehouse: ["returns", "warehouse"] as const,
  check: (id?: string) => ["returns", "warehouse", "check", id] as const,
  policies: ["returns", "policies"] as const,
  settings: ["returns", "settings"] as const,
  templates: ["returns", "templates"] as const,
  notifications: ["returns", "notifications"] as const,
};

const COMPONENT = "Returns";

async function unwrap<T>(
  request: Promise<DefaultResponseWithData<T>>,
): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message);
  return res.data;
}

/** A scanned or typed tag no. or tracking no. → the id of the return it belongs to (404 = no match, thrown). */
export function useWarehouseLookup() {
  const { get } = useApiHelper(apiConfig(COMPONENT));
  return useMutation((scan: string) =>
    unwrap(
      get<DefaultResponseWithData<string>>(
        `/returns/warehouse/lookup?scan=${encodeURIComponent(scan)}`,
      ),
    ),
  );
}

/** What the Received and checked section needs: part number, engine code and whether that check applies. */
export function useWarehouseCheck(id: string | undefined) {
  const { get } = useApiHelper(apiConfig(COMPONENT));
  const live = useReturnsLive();
  return useQuery<WarehouseCheckContext>(
    returnsWarehouseKeys.check(id),
    () =>
      unwrap(
        get<DefaultResponseWithData<WarehouseCheckContext>>(
          `/returns/warehouse/${encodeURIComponent(id as string)}`,
        ),
      ),
    {
      enabled: !!id && live,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
}

/** eBay's return business policies and return address, read-only (cached an hour on the API). */
export function useReturnPolicies() {
  const { get } = useApiHelper(apiConfig(COMPONENT));
  const live = useReturnsLive();
  return useQuery<ReturnPoliciesResponse>(
    returnsWarehouseKeys.policies,
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnPoliciesResponse>>(
          "/returns/policies",
        ),
      ),
    {
      enabled: live,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
}

export function useReturnsSettings() {
  const { get, put } = useApiHelper(apiConfig(COMPONENT));
  const queryClient = useQueryClient();
  const live = useReturnsLive();

  const settings = useQuery<ReturnsSettings>(
    returnsWarehouseKeys.settings,
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnsSettings>>("/returns/settings"),
      ),
    {
      enabled: live,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const saveSettings = useMutation(
    (body: ReturnsSettings) =>
      unwrap(
        put<ReturnsSettings, DefaultResponseWithData<ReturnsSettings>>(
          "/returns/settings",
          body,
        ),
      ),
    {
      onSettled: () => queryClient.invalidateQueries(returnsWarehouseKeys.all),
    },
  );

  return { settings, saveSettings };
}

export function useReturnTemplates() {
  const { get, put } = useApiHelper(apiConfig(COMPONENT));
  const queryClient = useQueryClient();
  const live = useReturnsLive();

  const templates = useQuery<ReturnTemplate[]>(
    returnsWarehouseKeys.templates,
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnTemplate[]>>("/returns/templates"),
      ),
    {
      enabled: live,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const saveTemplates = useMutation(
    (body: ReturnTemplate[]) =>
      unwrap(
        put<ReturnTemplate[], DefaultResponseWithData<ReturnTemplate[]>>(
          "/returns/templates",
          body,
        ),
      ),
    {
      onSettled: () => queryClient.invalidateQueries(returnsWarehouseKeys.all),
    },
  );

  return { templates, saveTemplates };
}

/**
 * The bell's returns rows (last 7 days). Only asked for when the customer's Returns flag is
 * enabled, so a Wilma without it never calls an endpoint that would answer 403.
 */
export function useReturnNotifications() {
  const { get, post } = useApiHelper(apiConfig(COMPONENT));
  const queryClient = useQueryClient();
  const enabled = useFeatureEnabled(RETURNS_FEATURE);

  const notifications = useQuery<ReturnNotification[]>(
    returnsWarehouseKeys.notifications,
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnNotification[]>>(
          "/returns/notifications",
        ),
      ),
    {
      enabled,
      refetchInterval: 5 * 60_000,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const markNotificationsSeen = useMutation(
    () =>
      post<Record<string, never>, DefaultResponseWithData<unknown>>(
        "/returns/notifications/seen",
        {},
      ),
    {
      onSettled: () =>
        queryClient.invalidateQueries(returnsWarehouseKeys.notifications),
    },
  );

  return { notifications, markNotificationsSeen, enabled };
}
